import type {
  EntityKey,
  OutputSchema,
  OutputType,
  PayloadTargetTemplate,
  TargetPayloadField,
  OutputField,
} from "./types";

export const API_SCOPE =
  process.env.REACT_APP_API_SCOPE ||
  import.meta.env.VITE_API_SCOPE ||
  "api://b4d2b9e7-957c-46dd-976c-9eec1f46ac72/jdx_portal_user_impersonation";

export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || "";

export const DEFAULT_BRANCH_ID = "10c20afe-fc39-4500-8ee8-268c9c510f68";

export const entities: { key: EntityKey; label: string }[] = [
  { key: "docforce", label: "Docforce" },
  { key: "customer-kpi", label: "Customer KPI" },
  { key: "finance", label: "Finance" },
];

export const blockGroups = [
  { title: "Triggers", blocks: ["When a new email arrives", "Recurrence", "HTTP"] },
  { title: "JDX Blocks", blocks: ["Select Customer", "Create Shipment Booking", "JDX Data Loader"] },
  { title: "Logic", blocks: ["Condition", "Switch", "Set Variable"] },
  { title: "Data", blocks: ["SQL Writer", "Storage Writer", "Blob Reader"] },
  { title: "Outputs", blocks: ["Output Builder", "HTTP Response", "HTTP Push", "Excel Export", "JSON Output", "CSV Export", "Blob Storage"] },
  { title: "Notifications", blocks: ["Teams Alert", "Email Alert"] },
];

export const outputTypes: Record<OutputType, string> = {
  http: "HTTP Response",
  "http-push": "HTTP Push / Webhook",
  excel: "Excel Export",
  json: "JSON Payload",
  csv: "CSV File",
  sql: "SQL Table",
  blob: "Blob Storage",
};

export const outputSchemas: Record<OutputSchema, { label: string; description: string; fields: OutputField[] }> = {
  "shipment-booking": {
    label: "Shipment booking",
    description: "Output fields for booking and shipment-level data.",
    fields: [
      { key: "customerReference", label: "Customer reference", group: "Booking" },
      { key: "modality", label: "Modality", group: "Booking" },
      { key: "loadType", label: "Load type", group: "Booking" },
      { key: "movementType", label: "Movement type", group: "Booking" },
      { key: "cargoReadyDate", label: "Cargo ready date", group: "Dates" },
      { key: "targetedArrivalDate", label: "Targeted arrival date", group: "Dates" },
      { key: "originPort.unLoCode", label: "Origin port", group: "Ports" },
      { key: "destinationPort.unLoCode", label: "Destination port", group: "Ports" },
      { key: "incoterm", label: "Incoterm", group: "Commercial" },
      { key: "shipperAddress.name", label: "Shipper", group: "Addresses" },
      { key: "consigneeAddress.name", label: "Consignee", group: "Addresses" },
      { key: "remarks", label: "Remarks", group: "Booking" },
    ],
  },
  "container-overview": {
    label: "Container overview",
    description: "Output fields for container, package and cargo reporting.",
    fields: [
      { key: "containers[].number", label: "Container number", group: "Containers" },
      { key: "containers[].containerType.isoCode", label: "Container type", group: "Containers" },
      { key: "containers[].sealNumber", label: "Seal number", group: "Containers" },
      { key: "containers[].goodsDescription", label: "Goods description", group: "Cargo" },
      { key: "containers[].gross", label: "Gross weight", group: "Cargo" },
      { key: "containers[].net", label: "Net weight", group: "Cargo" },
      { key: "containers[].volume", label: "Volume", group: "Cargo" },
      { key: "containers[].packages[].packageType", label: "Package type", group: "Packages" },
      { key: "containers[].packages[].quantity", label: "Package quantity", group: "Packages" },
      { key: "containers[].packages[].hsCodes[]", label: "HS codes", group: "Customs" },
    ],
  },
  custom: {
    label: "Custom schema",
    description: "Start from detected payload fields and select exactly what you need.",
    fields: [],
  },
};

export const targetPayloadTemplates: Record<PayloadTargetTemplate, { label: string; description: string; fields: TargetPayloadField[] }> = {
  "shipment-booking": {
    label: "Create shipment booking",
    description: "Receive a customer HTTP payload and generate a valid JDX Create shipment booking request.",
    fields: [
      { path: "branchId", label: "Branch ID", group: "Booking", required: true, description: "ID of the JDX branch. UUID." },
      { path: "customer.id", label: "Customer ID", group: "Booking", required: true, description: "ID of the customer in JDX." },
      { path: "modality", label: "Modality", group: "Booking", required: true, enumValues: ["OCEAN", "AIR", "ROAD", "RAIL"] },
      { path: "loadType", label: "Load type", group: "Booking", required: true, enumValues: ["FCL", "LCL", "FTL", "LTL", "ULD", "LOOSE"], note: "Determines whether containers or loose packages are expected." },
      { path: "movementType", label: "Movement type", group: "Booking", required: true, enumValues: ["EXPORT", "IMPORT", "CROSSTRADE"] },
      { path: "cargoReadyDate", label: "Cargo ready date", group: "Dates", required: true, description: "Local date-time without timezone." },
      { path: "targetedArrivalDate", label: "Targeted arrival date", group: "Dates", required: true, description: "Local date-time without timezone." },
      { path: "customerReference", label: "Customer reference", group: "References", required: true },
      { path: "agent.id", label: "Agent ID", group: "References", description: "Required only when an agent object is provided." },
      { path: "agentReference", label: "Agent reference", group: "References" },
      { path: "shipperAddress.name", label: "Shipper name", group: "Shipper address", condition: "Required when shipperAddress is provided." },
      { path: "shipperAddress.streetAddress", label: "Shipper street", group: "Shipper address", condition: "Required when shipperAddress is provided." },
      { path: "shipperAddress.city", label: "Shipper city", group: "Shipper address", condition: "Required when shipperAddress is provided." },
      { path: "shipperAddress.country", label: "Shipper country", group: "Shipper address", enumValues: ["ISO-3166-1 alpha-2"], condition: "Required when shipperAddress is provided." },
      { path: "consigneeAddress.name", label: "Consignee name", group: "Consignee address", condition: "Required when consigneeAddress is provided." },
      { path: "consigneeAddress.streetAddress", label: "Consignee street", group: "Consignee address", condition: "Required when consigneeAddress is provided." },
      { path: "consigneeAddress.city", label: "Consignee city", group: "Consignee address", condition: "Required when consigneeAddress is provided." },
      { path: "consigneeAddress.country", label: "Consignee country", group: "Consignee address", enumValues: ["ISO-3166-1 alpha-2"], condition: "Required when consigneeAddress is provided." },
      { path: "notifyPartyAddress.name", label: "Notify party name", group: "Notify party address", condition: "Required when notifyPartyAddress is provided." },
      { path: "notifyPartyAddress.streetAddress", label: "Notify party street", group: "Notify party address", condition: "Required when notifyPartyAddress is provided." },
      { path: "notifyPartyAddress.city", label: "Notify party city", group: "Notify party address", condition: "Required when notifyPartyAddress is provided." },
      { path: "notifyPartyAddress.country", label: "Notify party country", group: "Notify party address", enumValues: ["ISO-3166-1 alpha-2"], condition: "Required when notifyPartyAddress is provided." },
      { path: "originPort.unLoCode", label: "Origin UN/LOCODE", group: "Ports", required: true, description: "Exactly 5 characters." },
      { path: "destinationPort.unLoCode", label: "Destination UN/LOCODE", group: "Ports", required: true, description: "Exactly 5 characters." },
      { path: "incoterm", label: "Incoterm", group: "Commercial", required: true, enumValues: ["EXW", "FCA", "FOB", "CFR", "CPT", "CIF", "DPU", "DAP", "DDP", "OTHER"] },
      { path: "originOptions.hasDeclaration", label: "Origin customs declaration", group: "Commercial", condition: "Required when originOptions is provided." },
      { path: "originOptions.hasCarriage", label: "Origin pickup/carriage", group: "Commercial", condition: "Required when originOptions is provided." },
      { path: "destinationOptions.hasDeclaration", label: "Destination customs declaration", group: "Commercial", condition: "Required when destinationOptions is provided." },
      { path: "destinationOptions.hasCarriage", label: "Destination delivery/carriage", group: "Commercial", condition: "Required when destinationOptions is provided." },
      { path: "customsClearance[]", label: "Customs clearance", group: "Customs" },
      { path: "pickupAddress.name", label: "Pickup address name", group: "Pickup", condition: "Required when pickupAddress is provided." },
      { path: "pickupAddress.streetAddress", label: "Pickup street", group: "Pickup", condition: "Required when pickupAddress is provided." },
      { path: "pickupAddress.city", label: "Pickup city", group: "Pickup", condition: "Required when pickupAddress is provided." },
      { path: "pickupAddress.country", label: "Pickup country", group: "Pickup", enumValues: ["ISO-3166-1 alpha-2"], condition: "Required when pickupAddress is provided." },
      { path: "pickupTransportMode", label: "Pickup transport mode", group: "Pickup", enumValues: ["TRUCK", "BARGE", "RAIL"] },
      { path: "deliveryAddress.name", label: "Delivery address name", group: "Delivery", condition: "Required when deliveryAddress is provided." },
      { path: "deliveryAddress.streetAddress", label: "Delivery street", group: "Delivery", condition: "Required when deliveryAddress is provided." },
      { path: "deliveryAddress.city", label: "Delivery city", group: "Delivery", condition: "Required when deliveryAddress is provided." },
      { path: "deliveryAddress.country", label: "Delivery country", group: "Delivery", enumValues: ["ISO-3166-1 alpha-2"], condition: "Required when deliveryAddress is provided." },
      { path: "deliveryTransportMode", label: "Delivery transport mode", group: "Delivery", enumValues: ["TRUCK", "BARGE", "RAIL"] },
      { path: "remarks", label: "Remarks", group: "Booking", description: "Maximum 1000 characters." },
      { path: "containers[].containerType.isoCode", label: "Container ISO code", group: "Containers / full load", condition: "Use containers when loadType is FCL or another full-load type." },
      { path: "containers[].number", label: "Container number", group: "Containers / full load" },
      { path: "containers[].quantity", label: "Container quantity", group: "Containers / full load", required: true, condition: "Required for each container row." },
      { path: "containers[].goodsDescription", label: "Container goods description", group: "Containers / full load" },
      { path: "containers[].gross", label: "Container gross weight", group: "Containers / full load" },
      { path: "containers[].net", label: "Container net weight", group: "Containers / full load" },
      { path: "containers[].volume", label: "Container volume", group: "Containers / full load" },
      { path: "containers[].orderNumber", label: "Container order number", group: "Containers / full load" },
      { path: "containers[].sealNumber", label: "Seal number", group: "Containers / full load" },
      { path: "containers[].marksAndNumbers", label: "Marks and numbers", group: "Containers / full load", description: "Maximum 60 characters." },
      { path: "containers[].hsCodes[]", label: "Container HS codes", group: "Customs" },
      { path: "containers[].dgEnabled", label: "Container dangerous goods", group: "Dangerous goods" },
      { path: "containers[].unCodes[]", label: "Container UN codes", group: "Dangerous goods", condition: "Required when dgEnabled is true." },
      { path: "containers[].packages[].packageType", label: "Container package type", group: "Packages inside containers" },
      { path: "containers[].packages[].quantity", label: "Container package quantity", group: "Packages inside containers" },
      { path: "containers[].packages[].goodsDescription", label: "Container package goods description", group: "Packages inside containers" },
      { path: "containers[].packages[].volume", label: "Container package volume", group: "Packages inside containers" },
      { path: "containers[].packages[].weight", label: "Container package gross weight", group: "Packages inside containers" },
      { path: "containers[].packages[].net", label: "Container package net weight", group: "Packages inside containers" },
      { path: "containers[].packages[].hsCodes[]", label: "Container package HS codes", group: "Customs" },
    ],
  },
  "patch-cargo": {
    label: "Patch cargo",
    description: "Map an incoming customer cargo update to the JDX patch cargo payload.",
    fields: [
      { path: "containers[].id", label: "Container ID", group: "Containers", required: true },
      { path: "containers[].containerType.isoCode", label: "Container ISO code", group: "Containers" },
      { path: "containers[].number", label: "Container number", group: "Containers" },
      { path: "containers[].sealNumber", label: "Seal number", group: "Containers" },
      { path: "containers[].gross", label: "Gross weight", group: "Cargo" },
      { path: "containers[].net", label: "Net weight", group: "Cargo" },
      { path: "containers[].volume", label: "Volume", group: "Cargo" },
      { path: "containers[].currency", label: "Currency", group: "Commercial" },
      { path: "containers[].commercialValue", label: "Commercial value", group: "Commercial" },
      { path: "containers[].goodsDescription", label: "Goods description", group: "Cargo" },
      { path: "containers[].packages[].id", label: "Package ID", group: "Packages" },
      { path: "containers[].packages[].quantity", label: "Package quantity", group: "Packages" },
      { path: "containers[].packages[].packageType", label: "Package type", group: "Packages" },
      { path: "containers[].packages[].hsCodes[]", label: "HS codes", group: "Customs" },
    ],
  },
};
